import {
  CalendarHeaderViewModel,
  CalendarModeOptions,
} from 'frontcore/presentation/calendar/domain/types'
import { computed, ComputedRef, reactive, Ref, ref } from 'vue'
import { getLanguage } from 'frontcore/i18n'

interface ICalendarHeaderUseCase {
  viewmodel: CalendarHeaderViewModel
  changeCalendarMode: (modeValue: CalendarModeOptions) => void
  goToMonth: (monthValue: number) => void
  goToToday: () => void
  goToYear: (yearValue: number) => void
  nextMonth: () => void
  nextYear: () => void
  nextYearList: () => void
  previousMonth: () => void
  previousYear: () => void
  previousYearList: () => void
  setCalendarHeaderInfo: (monthValue: number, yearValue: number) => void
  showTodayButton: ComputedRef<boolean>
}

const useCalendarHeader = (
  mode: CalendarModeOptions,
  month: number,
  year: number,
  numberOfVisibleYears = 0,
  selectionMode: Ref<CalendarModeOptions> = ref(CalendarModeOptions.SELECT_DAY)
): ICalendarHeaderUseCase => {

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const previousMonth = () => {
    if (viewmodel.month === 1) {
      viewmodel.year -= 1
      viewmodel.month = 12
    } else {
      viewmodel.month -= 1
    }
  }

  const nextMonth = () => {
    if (viewmodel.month === 12) {
      viewmodel.year += 1
      viewmodel.month = 1
    } else {
      viewmodel.month += 1
    }
  }

  const nextYear = () => {
    viewmodel.year += 1
  }

  const nextYearList = () => {
    const firstYearOfTheList = viewmodel.yearList[numberOfVisibleYears - 1]
    if (firstYearOfTheList === undefined) { return }
    viewmodel.yearList = yearList(firstYearOfTheList + numberOfVisibleYears - 1)
  }

  const previousYear = () => {
    viewmodel.year -= 1
  }

  const previousYearList = () => {
    const lastYearOfTheList = viewmodel.yearList[0]
    if (lastYearOfTheList === undefined) { return }
    viewmodel.yearList = yearList(lastYearOfTheList)
  }

  const goToToday = () => {
    viewmodel.year = today.getFullYear()
    viewmodel.month = today.getMonth() + 1
    viewmodel.mode = CalendarModeOptions.SELECT_DAY
  }

  const goToMonth = (monthValue: number) => {
    if (monthValue > 11) return
    viewmodel.month = monthValue + 1
    if (selectionMode.value !== CalendarModeOptions.SELECT_MONTH) {
      viewmodel.mode = CalendarModeOptions.SELECT_DAY
    }
  }

  const goToYear = (yearValue: number) => {
    viewmodel.year = yearValue
    viewmodel.mode = CalendarModeOptions.SELECT_MONTH
  }

  const monthName = computed(() => {
    const firstDayOfTheMonth = new Date(
      viewmodel.year,
      viewmodel.month - 1,
      1
    )

    return firstDayOfTheMonth.toLocaleString(getLanguage(), { month: 'short' })
  })

  const yearList = (toYear: number) => {
    const years: number[] = []
    const initialYear = toYear - numberOfVisibleYears + 1
    for (let i=0; i < numberOfVisibleYears; i++) {
      years.push(initialYear + i)
    }
    return years
  }

  const changeCalendarMode = (modeValue: CalendarModeOptions) => {
    viewmodel.mode = modeValue
  }

  const viewmodel: CalendarHeaderViewModel = reactive({
    mode,
    month,
    monthName,
    year,
    yearList: yearList(year),
  })

  const setCalendarHeaderInfo = (monthValue: number, yearValue: number) => {
    if (viewmodel.month !== monthValue) {
      viewmodel.month = monthValue
    }
    if (viewmodel.year !== yearValue) {
      viewmodel.year = yearValue
    }
  }

  const showTodayButton = computed(() => {
    return selectionMode.value === CalendarModeOptions.SELECT_DAY
  })

  return {
    viewmodel,
    changeCalendarMode,
    goToMonth,
    goToToday,
    goToYear,
    nextMonth,
    nextYear,
    nextYearList,
    previousMonth,
    previousYear,
    previousYearList,
    setCalendarHeaderInfo,
    showTodayButton,
  }
}

export default useCalendarHeader
